import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { GraphQLEditor } from '@components/graphql-editor';
import { IconButton } from '@components/icon-button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCopyContent } from '@components/icons/copy-content';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useClipboard } from '@hooks/use-clipboard';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { useTravellerActionIntegrationResponses } from 'api/traveller-actions/hooks';
import { TravellerActionIntegrationResponse } from 'api/traveller-actions/requests';

import { getStatusCodeBadgeVariant } from './status-code-variant';

export const useRequestDetailModal = () => useModal(RequestDetailModal, { variant: 'LARGE' });

type RequestDetailModalProps = {
  actionId: string;
  responseShortId: string;
  initialResponse: TravellerActionIntegrationResponse;
};

const RequestDetailModal = ({
  actionId,
  responseShortId,
  initialResponse,
}: RequestDetailModalProps) => {
  const { t } = useScopedTranslate(locale);
  const { data: responses } = useTravellerActionIntegrationResponses(actionId);
  const response =
    responses?.find((candidate) => candidate.shortId === responseShortId) ?? initialResponse;
  const requestTypeLabel = (response.requestType ?? '').toUpperCase() || t('unknownType');

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={4}>
          <Stack space={2} orientation="horizontal" align="center">
            <Badge
              variant={getStatusCodeBadgeVariant(response.statusCode)}
              text={response.statusCode ?? t('noStatus')}
            />
            <Text font="regular14" color="textHelper" text={requestTypeLabel} />
            <Text
              font="regular14"
              color="textHelper"
              text={abbreviateDate(response.dateCreated, 'en', 'datetime-seconds')}
            />
          </Stack>
          {response.url && <UrlRow url={response.url} />}
          <RequestSection response={response} />
          <ResponseSection response={response} />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack space={4} orientation="horizontal" justify="flex-end">
          <Button variant="secondary" onClick={hideModal} text={t('core.close')} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const UrlRow = ({ url }: { url: string }) => {
  const { t } = useScopedTranslate(locale);
  const { copied, onCopy } = useClipboard();
  return (
    <Stack space={2}>
      <Text font="bold14" color="textSecondary" text={t('url')} />
      <Card variant="gray" spaceX={3} spaceY={2}>
        <Stack orientation="horizontal" align="center" justify="space-between" space={2}>
          <Text font="regular14" text={url} ellipsisWithTooltip />
          <IconButton
            variant="ghost-purple"
            icon={copied ? <IconCheckmark /> : <IconCopyContent />}
            onClick={() => onCopy(url)}
          />
        </Stack>
      </Card>
    </Stack>
  );
};

const RequestSection = ({ response }: { response: TravellerActionIntegrationResponse }) => {
  const { t } = useScopedTranslate(locale);
  const isGraphql = (response.endpointRequestType ?? '').toLowerCase() === 'graphql';
  const query = response.graphqlQuery ?? '';
  const showQuery = isGraphql && query.trim() !== '';

  return (
    <Stack space={3}>
      <Text font="heading2" text={t('request')} />
      {showQuery && <QueryBlock header={t('query')} query={query} />}
      <BodyCodeBlockOrEmpty
        data={response.requestData}
        header={isGraphql ? t('variables') : t('body')}
        emptyMessage={t('requestEmpty')}
      />
      {response.requestHeaders && Object.keys(response.requestHeaders).length > 0 && (
        <CodeBlock header={t('headers')} data={response.requestHeaders} />
      )}
    </Stack>
  );
};

const ResponseSection = ({ response }: { response: TravellerActionIntegrationResponse }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={3}>
      <Text font="heading2" text={t('response')} />
      <BodyCodeBlockOrEmpty
        data={response.responseData}
        header={t('body')}
        emptyMessage={t('responseEmpty')}
      />
      {response.responseHeaders && Object.keys(response.responseHeaders).length > 0 && (
        <CodeBlock header={t('headers')} data={response.responseHeaders} />
      )}
    </Stack>
  );
};

const BodyCodeBlockOrEmpty = ({
  data,
  header,
  emptyMessage,
}: {
  data: unknown;
  header: string;
  emptyMessage: string;
}) => {
  const hasData =
    data !== null &&
    data !== undefined &&
    data !== '' &&
    !(typeof data === 'object' && Object.keys(data as object).length === 0);

  if (!hasData) {
    return <Text font="regular14" color="textHelper" text={emptyMessage} />;
  }
  return <CodeBlock header={header} data={data as object} />;
};

const QueryBlock = ({ header, query }: { header: string; query: string }) => {
  const { copied, onCopy } = useClipboard();
  return (
    <QueryContainer>
      <HeaderFooterRow>
        <Text font="bold14" text={header} color="textSecondary" />
      </HeaderFooterRow>
      <EditorWrapper>
        <GraphQLEditor label="" initialValue={query} onChange={() => {}} isDisabled />
      </EditorWrapper>
      <HeaderFooterRow>
        <IconButton
          variant="ghost-purple"
          icon={copied ? <IconCheckmark /> : <IconCopyContent />}
          onClick={() => onCopy(query)}
        />
      </HeaderFooterRow>
    </QueryContainer>
  );
};

const QueryContainer = styled.div({
  border: `1px solid ${theme.color.border20}`,
  borderRadius: theme.space[2],
  backgroundColor: theme.color.uiTrans5,
});

const HeaderFooterRow = styled.div({
  padding: `${theme.space[2]} ${theme.space[3]}`,
});

const EditorWrapper = styled.div({
  borderTop: `1px solid ${theme.color.border20}`,
  borderBottom: `1px solid ${theme.color.border20}`,
  '.cm-content': {
    caretColor: 'transparent',
  },
  '.cm-editor': {
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    maxHeight: pxToRem(384),
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  '.cm-editor.cm-focused': {
    border: 'none',
    backgroundColor: 'transparent',
    outline: 'none',
  },
  '.cm-editor.cm-focused .cm-gutters': {
    backgroundColor: 'inherit',
  },
});

const locale = {
  en: {
    title: 'Request details',
    request: 'Request',
    response: 'Response',
    noStatus: 'No status',
    unknownType: 'Unknown',
    body: 'Body',
    query: 'Query',
    variables: 'Variables',
    headers: 'Headers',
    url: 'URL',
    requestEmpty: "This request doesn't have a body",
    responseEmpty: 'Empty response body',
  },
};
